import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { markConnectionExpired } from "../slice/accounting.slice";
import {
  getPlatformAccounts,
  getPlatformTaxRates,
  getPlatformTransactionStatuses,
  getPlatformCurrencies,
  getPlatformItems
} from "../api/accounting.api";

// Global cache for connection metadata promises to avoid multiple concurrent calls
const metadataPromiseCache = {};

export function useAccountingMetadata(connectionId, isExpired = false) {
  const dispatch = useDispatch();
  const [accountsList, setAccountsList] = useState([]);
  const [taxRatesList, setTaxRatesList] = useState([]);
  const [currenciesList, setCurrenciesList] = useState([]);
  const [invoiceStatusesList, setInvoiceStatusesList] = useState([]);
  const [poStatusesList, setPoStatusesList] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const activeFetchIdRef = useRef(0);

  const fetchMetadata = useCallback(async (connId) => {
    if (!connId || isExpired) {
      setAccountsList([]);
      setTaxRatesList([]);
      setCurrenciesList([]);
      setInvoiceStatusesList([]);
      setPoStatusesList([]);
      setItemsList([]);
      return;
    }

    if (!metadataPromiseCache[connId]) {
      metadataPromiseCache[connId] = (async () => {
        try {
          // Fetch primary accounts call first to verify authentication
          const accountsRes = await getPlatformAccounts(connId);

          // If accounts call succeeds, fetch remaining metadata in parallel
          const [taxRes, statusesRes, currenciesRes, itemsRes] = await Promise.all([
            getPlatformTaxRates(connId).catch(() => []),
            getPlatformTransactionStatuses(connId).catch(() => null),
            getPlatformCurrencies(connId).catch(() => []),
            getPlatformItems(connId).catch(() => [])
          ]);

          return [accountsRes, taxRes, statusesRes, currenciesRes, itemsRes];
        } catch (err) {
          // Dispatch markConnectionExpired to update Redux UI real-time to EXPIRED
          dispatch(markConnectionExpired(connId));
          delete metadataPromiseCache[connId];
          return [[], [], null, [], []];
        }
      })();
    }

    const fetchId = ++activeFetchIdRef.current;
    setLoadingMetadata(true);

    try {
      const [accountsRes, taxRes, statusesRes, currenciesRes, itemsRes] = await metadataPromiseCache[connId];

      if (fetchId !== activeFetchIdRef.current) return;

      const accounts = accountsRes?.data || accountsRes || [];
      const taxRates = taxRes?.data || taxRes || [];

      const currencies = currenciesRes?.data || currenciesRes || [];
      const invoiceStatuses = statusesRes?.data?.invoiceStatuses || statusesRes?.invoiceStatuses || [];
      const poStatuses = statusesRes?.data?.poStatuses || statusesRes?.poStatuses || [];
      const items = itemsRes?.data || itemsRes || [];

      setAccountsList(accounts);
      setTaxRatesList(taxRates);
      setCurrenciesList(currencies);
      setInvoiceStatusesList(invoiceStatuses);
      setPoStatusesList(poStatuses);
      setItemsList(items);
    } catch (err) {
      console.error("Failed to fetch connection metadata:", err);
      delete metadataPromiseCache[connId];
      if (fetchId === activeFetchIdRef.current) {
        setAccountsList([]);
        setTaxRatesList([]);
        setCurrenciesList([]);
        setInvoiceStatusesList([]);
        setPoStatusesList([]);
        setItemsList([]);
      }
    } finally {
      if (fetchId === activeFetchIdRef.current) {
        setLoadingMetadata(false);
      }
    }
  }, [isExpired]);

  useEffect(() => {
    fetchMetadata(connectionId);
  }, [connectionId, fetchMetadata]);

  return {
    accountsList,
    taxRatesList,
    currenciesList,
    invoiceStatusesList,
    poStatusesList,
    itemsList,
    loadingMetadata,
    refreshMetadata: () => {
      if (connectionId) {
        delete metadataPromiseCache[connectionId];
        fetchMetadata(connectionId);
      }
    }
  };
}
